import type { QueryClient } from '@tanstack/react-query';
import { recordPage } from '../feedback/diagnostics';
import {
  createBrowserRouter,
  data,
  redirect,
  type LoaderFunctionArgs,
  type RouteObject,
} from 'react-router';
import {
  budgetsQuery,
  homeQuery,
  isApiError,
  mySettingsQuery,
  recognizeOptionsQuery,
  waitingQuery,
  type HomeView,
} from '../api';
import { doneForMeQuery } from '../api/actingFor';
import { awardQuery, myAwardsPageQuery, personCardQuery } from '../api/awards';
import {
  myElectionPreviewQuery,
  rewardQuery,
  rewardsPageQuery,
  type RewardsView,
} from '../api/rewards';
import { skipRevalidationFor } from '../ui/revalidate';
import { loaderPaging } from '../ui/usePaging';
import { handoutsQuery, handoutView, overdueElectionsQuery } from '../api/handouts';
import { personParam } from '../awards/person-param';
import { isEditableDraft } from '../awards/present';
import { backLink } from '../awards/record/present';
import { helpLinkRoutes } from '../help/linkRoutes';
import { HomePage, homeHelp } from '../home/HomePage';
import { QUEUE_TABLE_PARAMS } from '../ops/queue/sorting';
import { aiAdminRoutes } from '../admin/ai/routes';
import { legacyAdminRoute } from '../settings/LegacyAdminRedirect';
import { adminRoutes } from '../admin/routes';
import { askDataRoutes } from '../ask-data/routes';
import { closeRoutes } from '../close/routes';
import { insightsRoutes } from '../insights/routes';
import { hasManagePage, spotRecordArea } from '../manage/pages';
import { manageRoutes, movedSpotTab } from '../manage/routes';
import { opsRoutes } from '../ops/routes';
import { previewAware } from '../preview';
import { requireMe } from '../session/loaders';
import { executiveRoutes, settingsRoutes } from '../settings/routes';
import { trainingRoutes } from '../training/routes';
import { AppUnavailable } from '../signin/AppUnavailable';
import { NoAccessPage } from '../signin/NoAccessPage';
import { collectSearchPages, forOwnWork } from '../search/pages';
import { PublicLoading } from '../signin/PublicLoading';
import { SignInPage, signInLoader } from '../signin/SignInPage';
import { WelcomePage, welcomeLoader } from '../signin/WelcomePage';
import { AppLoading } from './AppLoading';
import { itemFromState, opensFrom, ownerOfPath, withNavItem, type NavContext } from './nav';
import { NotFoundPage, RouteError } from './NotFoundPage';
import { RootLayout, type RootHandle, type RouteHandle } from './RootLayout';

// The design-system gallery exists in development builds only: `import.meta.env.DEV` is replaced
// with `false` in production, so the route and its lazy chunk are dropped from the bundle.
const devRoutes: RouteObject[] = import.meta.env.DEV
  ? [
      {
        path: 'dev/gallery/*',
        handle: {
          help: { pageLabel: 'Design system gallery', topicIds: ['waiting', 'search'] },
          // Outside the menus: a development tool, not a page anyone works in.
          navItem: null,
        } satisfies RouteHandle,
        lazy: async () => {
          const { Gallery } = await import('../dev/Gallery');
          return { Component: Gallery };
        },
      },
    ]
  : [];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A list paged on the server: once a new rows per page is saved, the size leaves the address and
 * the loader would read the same page again, so it doesn't run (APP-135 §7).
 */
const sizeSaved = skipRevalidationFor([], { sizeRemoved: ['size'] });

/** What the award page's loader tells the header about the award (QA-045). */
interface AwardFacts {
  /** A draft its owner can still edit: the entry form, under Recognize. */
  editable: boolean;
  /** Entered by this person, or for them. */
  mine: boolean;
}

/**
 * The award page belongs where it was opened from, the same place its back link goes: the queue
 * or a check under Manage, All awards under Insights, My drafts & submitted under Recognize. A draft
 * being edited is the entry form, under Recognize.
 */
function awardNavItem({ state, data, me, ownerOf }: NavContext): ReturnType<typeof ownerOf> {
  const facts = data as AwardFacts | null | undefined;
  if (facts?.editable === true) return 'recognize';
  // The item the opening page had current, as its link said; else the back link's page's.
  const said = itemFromState(state, me);
  if (said !== null) return said;
  const back = backLink(state, { mine: facts?.mine ?? false, roles: me.roles.map((r) => r.role) });
  return ownerOf(back.href);
}

const HANDOUTS = 'letters-to-hand-out';
/**
 * APP-245 D6: Letters to hand out is under Manage while it's one of the person's Manage pages (they
 * hold letters), as Approvals is; otherwise under Home, where the account menu opened it before.
 */
function handoutsNavItem({ me }: NavContext): ReturnType<NavContext['ownerOf']> {
  return me.nav.includes('manage') && hasManagePage(me, HANDOUTS) ? 'manage' : 'home';
}

/** Answers 404 so the in-app not-found page shows (RouteError). */
function notFound(): never {
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router's 404 response
  throw data(null, { status: 404 });
}

/**
 * Recognize, the award page and My drafts & submitted (SL2–SL6, R2, D1, G1). Each loads its code
 * on first use (APP-008) and its data before it renders.
 */
function awardRoutes(queryClient: QueryClient): RouteObject[] {
  const recognizeHandle: RouteHandle = {
    help: { pageLabel: 'Recognize', topicIds: ['award-entry', 'over-budget', 'paid-from', 'spot'] },
    search: { label: 'Recognize', meta: 'Spot nominations and new awards' },
    navItem: 'recognize',
  };
  const awardHandle: RouteHandle = {
    help: {
      pageLabel: 'Award',
      topicIds: ['award-entry', 'award-record', 'paid-from', 'in-progress'],
    },
    navItem: awardNavItem,
    opensFrom: true,
  };
  return [
    {
      path: 'recognize',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        const pools = me.roles.some((r) =>
          ['sector_lead', 'executive', 'stock_admin', 'finance', 'system_admin'].includes(r.role),
        );
        const person = personParam(new URL(request.url).searchParams);
        await Promise.all([
          queryClient.query(recognizeOptionsQuery),
          pools ? queryClient.query(budgetsQuery).catch(() => null) : null,
          // Someone who can't be found just isn't added; the page says so.
          person === null ? null : queryClient.query(personCardQuery(person)).catch(() => null),
        ]);
        return null;
      },
      lazy: async () => {
        const { RecognizePage } = await import('../awards/RecognizePage');
        return { Component: RecognizePage };
      },
      handle: recognizeHandle,
    },
    {
      path: 'awards',
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireMe(queryClient, request);
        const params = new URL(request.url).searchParams;
        const year = params.get('year');
        await Promise.all([
          queryClient.query(
            myAwardsPageQuery({
              year: year !== null && /^\d{1,5}$/.test(year) ? Number(year) : null,
              ...(await loaderPaging(queryClient, params, 'sent_awards')),
            }),
          ),
          queryClient.query(recognizeOptionsQuery),
        ]);
        return null;
      },
      shouldRevalidate: sizeSaved,
      lazy: async () => {
        const { MyAwardsPage } = await import('../awards/MyAwardsPage');
        return { Component: MyAwardsPage };
      },
      handle: {
        help: { pageLabel: 'My drafts & submitted', topicIds: ['award-entry', 'in-progress'] },
        search: {
          label: 'My drafts & submitted',
          meta: 'Awards you started or sent',
          visible: forOwnWork('sector_lead', 'delegate', 'executive', 'executive_delegate'),
        },
        navItem: 'recognize',
      } satisfies RouteHandle,
    },
    {
      path: 'awards/:id',
      loader: async ({ request, params }: LoaderFunctionArgs): Promise<AwardFacts> => {
        const me = await requireMe(queryClient, request);
        const id = params['id'] ?? '';
        if (!UUID.test(id)) notFound();
        try {
          const view = await queryClient.query(awardQuery(id));
          const editable = isEditableDraft(view);
          if (editable) await queryClient.query(recognizeOptionsQuery);
          return {
            editable,
            mine: view.createdBy.id === me.person.id || view.onBehalfOf?.id === me.person.id,
          };
        } catch (error) {
          if (isApiError(error) && error.status === 404) notFound();
          throw error;
        }
      },
      lazy: async () => {
        const { AwardPage } = await import('../awards/AwardPage');
        return { Component: AwardPage };
      },
      handle: awardHandle,
    },
  ];
}

/** Purpose-specific own-line routes. Staff roles never select a broader award endpoint here. */
function rewardRoutes(queryClient: QueryClient): RouteObject[] {
  const handle: RouteHandle = {
    help: { pageLabel: 'My rewards', topicIds: ['election', 'letters', 'award-record'] },
  };
  const list =
    (view: RewardsView) =>
    async ({ request }: LoaderFunctionArgs) => {
      await requireMe(queryClient, request);
      const params = new URL(request.url).searchParams;
      const { page, size } = await loaderPaging(queryClient, params, 'my_rewards');
      await Promise.all([
        queryClient.query(rewardsPageQuery({ view, page, size })),
        // The Elections tab shows how the choice works until one opens (E3.empty, APP-093). A
        // prefetch: if it can't load, the tab still lists the person's elections.
        ...(view === 'elections'
          ? [queryClient.query(myElectionPreviewQuery).catch(() => undefined)]
          : []),
      ]);
      return null;
    };
  const detail = async ({ request, params }: LoaderFunctionArgs) => {
    await requireMe(queryClient, request);
    const lineId = params['lineId'] ?? '';
    if (!UUID.test(lineId)) notFound();
    try {
      await queryClient.query(rewardQuery(lineId));
    } catch (error) {
      if (isApiError(error) && error.status === 404) notFound();
      throw error;
    }
    return null;
  };
  return [
    {
      path: 'my-rewards',
      loader: list('awards'),
      shouldRevalidate: sizeSaved,
      handle: {
        ...handle,
        search: { label: 'My rewards', meta: 'Your awards, elections and letters' },
      },
      lazy: async () => ({
        Component: (await import('../my-rewards/MyRewardsPage')).MyRewardsPage,
      }),
    },
    {
      path: 'my-rewards/elections',
      loader: list('elections'),
      shouldRevalidate: sizeSaved,
      handle: {
        ...handle,
        search: { label: 'Elections', meta: 'My rewards · your choices on awards with options' },
      },
      lazy: async () => ({
        Component: (await import('../my-rewards/MyRewardsPage')).RewardElectionsPage,
      }),
    },
    {
      path: 'my-rewards/documents',
      loader: list('documents'),
      shouldRevalidate: sizeSaved,
      handle: {
        ...handle,
        search: {
          label: 'Letters & statements',
          meta: 'My rewards · your award letters and earning statements',
        },
      },
      lazy: async () => ({
        Component: (await import('../my-rewards/MyRewardsPage')).RewardDocumentsPage,
      }),
    },
    {
      path: 'my-rewards/:lineId',
      loader: detail,
      handle,
      lazy: async () => ({ Component: (await import('../my-rewards/RewardPage')).RewardPage }),
    },
    {
      path: 'my-rewards/:lineId/election',
      loader: detail,
      handle,
      lazy: async () => ({ Component: (await import('../my-rewards/ElectionPage')).ElectionPage }),
    },
  ];
}

function homeViewFrom(request: Request): HomeView {
  return import.meta.env.DEV && new URL(request.url).searchParams.get('view') === 'first-run'
    ? 'first-run'
    : 'live';
}

/**
 * Every route. The sign-in pages sit outside the shell and never need a session (so a 401 can't
 * loop); everything under `/` needs one, and its loader redirects to sign-in without it.
 */
export function createRoutes(queryClient: QueryClient): RouteObject[] {
  const homeHandle: RouteHandle = {
    help: homeHelp,
    search: { label: 'Home', meta: 'Waiting on you and everything that needs you' },
    navItem: 'home',
  };
  // The signed-in pages, inside the shell and its error boundary.
  const shell: RouteObject[] = [
    {
      index: true,
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        await Promise.all([
          queryClient.query(waitingQuery),
          queryClient.query(homeQuery(me, homeViewFrom(request))),
          // Waiting on you's order and a kept sort are the person's settings (APP-149 D7): read
          // first, so Home opens in them. If they can't be read, Home opens oldest first.
          queryClient.query(mySettingsQuery).catch(() => undefined),
        ]);
        return null;
      },
      // The queue card sorts and filters on the screen (APP-149): its params need no reload.
      shouldRevalidate: skipRevalidationFor(QUEUE_TABLE_PARAMS),
      Component: HomePage,
      handle: homeHandle,
    },
    ...awardRoutes(queryClient),
    {
      // APP-234: the Spot work tabs moved under Manage; an old `?tab=review` lands there with the
      // rest of its query. No page of its own, so the redirect works in a preview too. It runs for
      // the children too, so `movedSpotTab` moves the list address alone: a nomination's page keeps
      // its id and query.
      path: 'recognize/spot',
      loader: ({ request }: LoaderFunctionArgs) => {
        const moved = movedSpotTab(new URL(request.url));
        return moved === null ? null : redirect(moved);
      },
      handle: { navItem: 'recognize' } satisfies RouteHandle,
      children: (
        [
          // A new nomination, My nominations and Received: what every employee does.
          [undefined, 'recognize'],
          // A nomination's own page: the author's, a nominee's, or the committee's review (D5b).
          [':id', ({ state, me }) => spotRecordArea(state, me)],
          // A supervisor's decision on one colleague: Manage › Spot decisions.
          [':id/supervision/:personId', 'manage'],
        ] as const satisfies readonly (readonly [string | undefined, RouteHandle['navItem']])[]
      ).map(([path, navItem]): RouteObject => ({
        ...(path === undefined ? { index: true } : { path }),
        loader: async ({ request }: LoaderFunctionArgs) => {
          await requireMe(queryClient, request);
          return null;
        },
        handle: {
          help: { pageLabel: 'Spot nominations', topicIds: ['spot'] },
          navItem,
          ...(path === undefined && {
            search: {
              label: 'Spot nominations',
              meta: 'Recognize a colleague, drafts and nominations',
            },
          }),
        } satisfies RouteHandle,
        lazy: async () => ({ Component: (await import('../spot/SpotPage')).SpotPage }),
      })),
    },
    // Each area under the Level 1 item it belongs to (QA-045). APP-234: the Stock Admin team's
    // queue, the check, Payroll's queue and the work lists are Manage's; approvals belong to whoever
    // decides; the Stock Admin team's Home is still their queue (DECISION-LOG R3).
    ...withNavItem('manage', manageRoutes(queryClient)),
    ...withNavItem('manage', opsRoutes(queryClient)),
    ...withNavItem('manage', closeRoutes(queryClient)),
    ...withNavItem('settings', [legacyAdminRoute, ...adminRoutes(queryClient)]),
    ...withNavItem('settings', aiAdminRoutes(queryClient)),
    ...withNavItem('insights', askDataRoutes(queryClient)),
    ...withNavItem('recognize', helpLinkRoutes(queryClient)),
    ...withNavItem('settings', settingsRoutes(queryClient)),
    // APP-236: the Executive pages are Manage's Executive group.
    ...withNavItem('manage', executiveRoutes(queryClient)),
    ...withNavItem('my-rewards', rewardRoutes(queryClient)),
    ...withNavItem('insights', insightsRoutes(queryClient)),
    ...withNavItem('home', trainingRoutes(queryClient)),
    {
      path: 'letters-to-hand-out',
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireMe(queryClient, request);
        const search = new URL(request.url).searchParams;
        // The pages and rows per page each table will ask for, so these fetches are the page's own.
        const letters = await loaderPaging(queryClient, search, 'letters_to_hand_out');
        const late = await loaderPaging(queryClient, search, 'overdue_elections', {
          param: 'overduePage',
          sizeParam: 'overdueSize',
        });
        await Promise.all([
          queryClient.query(
            handoutsQuery({
              view: handoutView(search.get('view')),
              page: letters.page,
              size: letters.size,
            }),
          ),
          queryClient.query(
            overdueElectionsQuery({
              page: late.page,
              size: late.size,
            }),
          ),
        ]);
        return null;
      },
      // Two lists, each with its own rows per page (APP-135 §7).
      shouldRevalidate: skipRevalidationFor([], { sizeRemoved: ['size', 'overdueSize'] }),
      handle: {
        help: { pageLabel: 'Letters to hand out', topicIds: ['letters', 'election'] },
        search: {
          label: 'Letters to hand out',
          meta: 'Award letters to give your people',
          // APP-245: and anyone it's a Manage page for: whoever holds letters.
          visible: (me) => forOwnWork('manager')(me) || hasManagePage(me, HANDOUTS),
        },
        navItem: handoutsNavItem,
      } satisfies RouteHandle,
      lazy: async () => ({
        Component: (await import('../letters-to-hand-out/LettersToHandOutPage'))
          .LettersToHandOutPage,
      }),
    },
    {
      // APP-148 D2: what the Stock Admin team did for this person, every page of it. The email's
      // "Not what you asked?" link adds `?question=‹id›`, which opens on the page: no reload.
      path: 'done-for-you',
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireMe(queryClient, request);
        const params = new URL(request.url).searchParams;
        const paging = await loaderPaging(queryClient, params, 'done_for_you');
        // A failed read is the page's own to say, never the route's error page.
        await queryClient.query(doneForMeQuery(paging)).catch(() => null);
        return null;
      },
      shouldRevalidate: skipRevalidationFor(['question'], { sizeRemoved: ['size'] }),
      handle: {
        help: {
          pageLabel: 'Done for you',
          topicIds: ['act-for-others', 'waiting', 'award-record'],
        },
        search: {
          label: 'Done for you',
          meta: 'What the Stock Admin team did when you asked them to',
          visible: forOwnWork('executive', 'sector_lead'),
        },
        navItem: 'home',
      } satisfies RouteHandle,
      lazy: async () => ({
        Component: (await import('../acting-for/DoneForYouPage')).DoneForYouPage,
      }),
    },
    ...devRoutes,
    // Outside the menus: an address that isn't a page.
    { path: '*', Component: NotFoundPage, handle: { navItem: null } satisfies RouteHandle },
  ];
  return [
    {
      path: '/sign-in',
      loader: () => signInLoader(queryClient),
      Component: SignInPage,
      HydrateFallback: PublicLoading,
      ErrorBoundary: AppUnavailable,
    },
    {
      path: '/welcome',
      loader: ({ request }: LoaderFunctionArgs) => welcomeLoader(queryClient, request),
      Component: WelcomePage,
      HydrateFallback: PublicLoading,
      ErrorBoundary: AppUnavailable,
    },
    {
      path: '/no-access',
      Component: NoAccessPage,
      ErrorBoundary: AppUnavailable,
    },
    {
      id: 'root',
      path: '/',
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireMe(queryClient, request);
        return null;
      },
      Component: RootLayout,
      HydrateFallback: AppLoading,
      ErrorBoundary: AppUnavailable,
      handle: {
        searchPages: collectSearchPages(shell),
        navOwner: (path, me) => ownerOfPath([{ path: '/', children: shell }], path, me),
        opensFrom: (path) => opensFrom([{ path: '/', children: shell }], path),
      } satisfies RootHandle,
      // While previewing, page loaders fetch nothing; the shell shows sample layouts (APP-059).
      children: [{ ErrorBoundary: RouteError, children: previewAware(shell, queryClient) }],
    },
  ];
}

/** '/awards/:displayId' from the matched route chain; null when nothing matched. */
export function routePattern(matches: readonly { route: { path?: string } }[]): string | null {
  let pattern = '';
  for (const { route } of matches) {
    if (route.path === undefined) continue;
    pattern = route.path.startsWith('/')
      ? route.path
      : `${pattern.replace(/\/$/, '')}/${route.path}`;
  }
  return pattern === '' ? null : pattern;
}

export function createAppRouter(queryClient: QueryClient): ReturnType<typeof createBrowserRouter> {
  const router = createBrowserRouter(createRoutes(queryClient));
  // The feedback report's recent pages (APP-104 §4): path only, never the query.
  const note = (state: typeof router.state): void => {
    if (state.initialized && state.navigation.state === 'idle')
      recordPage(state.location.pathname, routePattern(state.matches));
  };
  note(router.state);
  router.subscribe(note);
  return router;
}
